import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { LoadingButton } from "@mui/lab";

import { LoansContext } from "../../context/LoansContext";
import { RootContext } from "../../context/RootContext";
import { SUPPORTED_DOCUMENT_ACCEPT, partitionBySupportedFileType } from "../../utils/fileValidation";

import {
  WaiverServiceRecord,
  fetchAndSyncWaivers,
  resetWaiverStatus,
  uploadWaiverDocuments,
  postWaiverCommentAsChat,
} from "./utils";

// Loan officer's reply to an underwriter's "Need Additional Info" request:
// a required comment plus optional extra documents, recorded as a
// "Re-Requested" response so the waiver is back with the underwriter.
export default function WaiverInfoResponseForm({ waiver }: { waiver: WaiverServiceRecord }) {
  const { t } = useTranslation();
  const { setLoanDetails, setWaiverDecisionVersion } = useContext(LoansContext) ?? {};
  const { decodedTokenData } = useContext(RootContext) ?? {};

  const [comment, setComment] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { supported, unsupported } = partitionBySupportedFileType(Array.from(event.target.files ?? []));
    setFileError(unsupported.length ? t("unsupportedFileType") : "");
    if (supported.length) setFiles((prev) => [...prev, ...supported]);
    event.target.value = "";
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      // Documents first, so they are on the waiver before it re-opens for the
      // underwriter.
      await uploadWaiverDocuments(setLoanDetails, waiver, files);
      await resetWaiverStatus(
        setLoanDetails,
        waiver,
        setWaiverDecisionVersion,
        decodedTokenData,
        comment.trim(),
      );
      await postWaiverCommentAsChat(waiver.waiverId, comment);
      await fetchAndSyncWaivers(waiver.sizerLoanId, setLoanDetails, setWaiverDecisionVersion, true);
    } catch (err) {
      setSubmitError(t("waiverActionFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box data-testid="waiver-info-response-form" sx={{ padding: "16px" }}>
      <span style={{ fontSize: "12px", lineHeight: "20px", letterSpacing: "0.4px", fontWeight: 300 }}>
        {t("waiverComments")}
        <sup style={{ color: "red" }}>*</sup>
      </span>
      <TextField
        fullWidth
        multiline
        minRows={3}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        inputProps={{ "data-testid": "waiver-info-response-comment" }}
        style={{ background: "#F8F9FD" }}
      />
      <Button component="label" variant="outlined" size="small" sx={{ marginTop: "12px" }}>
        {t("supportDocument")}
        <input
          hidden
          type="file"
          multiple
          accept={SUPPORTED_DOCUMENT_ACCEPT}
          onChange={handleFiles}
          data-testid="waiver-info-response-file-input"
        />
      </Button>
      {files.map((file, index) => (
        <div key={`${file.name}-${index}`} style={{ fontSize: "14px" }} data-testid="waiver-info-response-file">
          {file.name}
        </div>
      ))}
      {fileError ? <div style={{ color: "#ef4444", fontSize: "12px" }}>{fileError}</div> : null}
      {submitError ? <div style={{ color: "#ef4444", fontSize: "12px" }}>{submitError}</div> : null}
      <div style={{ marginTop: "12px" }}>
        <LoadingButton
          variant="contained"
          loading={isSubmitting}
          disabled={!comment.trim()}
          onClick={handleSubmit}
          data-testid="waiver-info-response-submit"
        >
          {t("submitAdditionalInfo")}
        </LoadingButton>
      </div>
    </Box>
  );
}
